
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Share2, LogIn, UserRound, Menu, Globe, BookOpen, Coins } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from "@/context/LanguageContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import AdminAccessButton from "@/components/admin/AdminAccessButton";

const Navbar = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { language, setLanguage, t } = useLanguage();
  
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const {
          data
        } = await supabase.auth.getSession();
        const loggedIn = !!data.session;
        setIsLoggedIn(loggedIn);
        console.log('Navbar: Auth status changed:', { loggedIn, userId: data.session?.user?.id });
      } catch (error) {
        console.error("Error checking auth status:", error);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
    const {
      data: authListener
    } = supabase.auth.onAuthStateChange((event, session) => {
      const loggedIn = !!session;
      setIsLoggedIn(loggedIn);
      console.log('Navbar: Auth state changed:', { event, loggedIn, userId: session?.user?.id });
    });
    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);
  
  const handleLogout = async () => {
    try {
      console.log("Attempting to log out...");
      await supabase.auth.signOut();
      toast({
        title: "已成功登出",
        description: "感謝您的使用！"
      });
      console.log("Successfully logged out");
      setIsLoggedIn(false);
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
      toast({
        variant: "destructive",
        title: "登出失敗",
        description: "請重試"
      });
    }
  };
  
  const languages = [
    { code: "zh-TW", label: "繁體中文" },
    { code: "en", label: "English" }
  ];
  
  const handleLanguageChange = (langCode: 'en' | 'zh-TW') => {
    setLanguage(langCode);
    setIsMobileMenuOpen(false);
  };
  
  const getGuidesLabel = () => {
    return language === 'en' ? 'Rental Guides' : '租屋攻略';
  };

  const getAboutLabel = () => {
    return language === 'en' ? 'About Us' : '關於我們';
  };

  const getPointsLabel = () => {
    return language === 'en' ? 'Points Center' : '積分中心';
  };
  
  const navigationLinks = [
    { to: "/reviews", label: t('common.reviews'), icon: null },
    { to: "/submit", label: t('common.submit'), icon: null },
    { to: "/guides", label: getGuidesLabel(), icon: null },
    { to: "/points", label: getPointsLabel(), icon: null },
    { to: "/about", label: getAboutLabel(), icon: null }
  ];
  
  const UserMenu = () => (
    <div className="flex items-center gap-3">
      <AdminAccessButton />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="relative h-8 w-8 rounded-full">
            <Avatar className="h-8 w-8">
              <AvatarFallback>
                <UserRound className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link to="/profile" className="cursor-pointer">
              <UserRound className="mr-2 h-4 w-4" />
              {t('common.profile')}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
            <LogIn className="mr-2 h-4 w-4 transform rotate-180" />
            {t('common.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
  
  return (
    <header className="sticky top-0 z-40 w-full border-b bg-white shadow-sm">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <span className="font-bold text-xl text-gray-900">TruStay</span>
        </Link>
        
        <div className="hidden md:flex items-center space-x-6 ml-auto">
          <Link to="/reviews">
            <Button variant="ghost" size="sm">
              <span>{t('common.reviews')}</span>
            </Button>
          </Link>
          <Link to="/submit">
            <Button variant="ghost" size="sm">
              <span>{t('common.submit')}</span>
            </Button>
          </Link>
          <Link to="/guides">
            <Button variant="ghost" size="sm">
              <span>{getGuidesLabel()}</span>
            </Button>
          </Link>
          <Link to="/points">
            <Button variant="ghost" size="sm">
              <span>{getPointsLabel()}</span>
            </Button>
          </Link>
          <Link to="/about">
            <Button variant="ghost" size="sm">
              <span>{t('common.about')}</span>
            </Button>
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="w-9 px-0">
                <Globe className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {languages.map(lang => (
                <DropdownMenuItem 
                  key={lang.code} 
                  onClick={() => handleLanguageChange(lang.code as 'en' | 'zh-TW')} 
                  className={language === lang.code ? "bg-accent" : ""}
                >
                  {lang.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {!loading && (
            <div className="hidden md:flex items-center gap-3">
              {isLoggedIn ? (
                <UserMenu />
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login">
                    <Button variant="outline" size="sm">
                      {t('common.login')}
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button size="sm" className="bg-primary-purple hover:bg-primary-purple/90 text-slate-50 bg-orange-500 hover:bg-orange-400">
                      {t('common.signup')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="md:hidden p-2">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[80vw] sm:w-[380px]">
              <nav className="flex flex-col gap-4 mt-8">
                {navigationLinks.map(link => (
                  <Link key={link.to} to={link.to}>
                    <Button variant="ghost" size="lg" className="w-full justify-start gap-2">
                      {link.icon && link.icon}
                      {link.label}
                    </Button>
                  </Link>
                ))}
                
                {loading ? (
                  <div className="h-10 bg-gray-100 animate-pulse rounded-md" />
                ) : isLoggedIn ? (
                  <>
                    <div className="px-2 py-2">
                      <AdminAccessButton />
                    </div>
                    <Link to="/profile">
                      <Button variant="outline" size="lg" className="w-full justify-start gap-2">
                        <UserRound className="h-4 w-4" />
                        {t('common.profile')}
                      </Button>
                    </Link>
                    <Button variant="ghost" size="lg" onClick={handleLogout} className="w-full justify-start gap-2">
                      <LogIn className="h-4 w-4 transform rotate-180" />
                      {t('common.logout')}
                    </Button>
                  </>
                ) : (
                  <div className="flex flex-col gap-2 w-full">
                    <Link to="/login" className="w-full">
                      <Button variant="outline" size="lg" className="w-full">
                        {t('common.login')}
                      </Button>
                    </Link>
                    <Link to="/signup" className="w-full">
                      <Button size="lg" className="w-full bg-primary-purple hover:bg-primary-purple/90 bg-orange-500 hover:bg-orange-400">
                        {t('common.signup')}
                      </Button>
                    </Link>
                  </div>
                )}
              </nav>

              <div className="fixed bottom-8 left-6 right-6 text-center text-sm text-muted-foreground">
                <div className="flex justify-center items-center gap-2">
                  <LogIn className="h-4 w-4" />
                  <span>customer.service@trustay.app</span>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
